import { useEffect, useRef, useState } from "react";
import { fetchCombinedSearch } from "../services/news.service";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getFilterDateValueKeys } from "utils/date-filter.util";
import { useSourceSearch } from "features/SourceSearch";
import { useDispatch } from "react-redux";
import { setCategories, setNoFetchMore, resetNoFetchMore } from "context/features/dataSlice";
import { biasRating } from "data/biasRatingLabel";
import { publishers } from "data/publishers";
import { getSourceConfig } from "config";
import {
  getDatabaseApiName,
  getDatabaseSearch,
  normalizeDatabaseSearchSections,
} from "../services/database.service";

const useNewsData = (
  setGlobalLoading,
  query,
  categoriesCopy,
  setLoadingStates
) => {
  const [combinedSearch, setCombinedSearch] = useState([]);
  const dispatch = useDispatch();
  const timeoutRef = useRef();
  const searchRequestRef = useRef(0);

  const { currentTimeframe, startDate, endDate } = useSourceSearch();
  const { source } = useParams();
  const { categories } = useSelector((state) => state.data);

  const filteredDateValue = getFilterDateValueKeys(
    currentTimeframe,
    startDate,
    endDate
  );

  const tempCategories = categoriesCopy.length > 0 ? source === "rating" ? biasRating : source === "partner-publishers" ? publishers : categories : [];

  const visibleCategoryNames = tempCategories
    .filter((cat) => !cat.isHidden_ysn)
    .map((cat) => cat.categoryName_chr || cat.label);

  const fetchCombinedSearchData = async () => {
    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;

    const isStaleRequest = () => requestId !== searchRequestRef.current;

    setGlobalLoading(true);
    setCombinedSearch([]);
    setLoadingStates({});
    dispatch(resetNoFetchMore());

    try {
      visibleCategoryNames.forEach((cat) =>
        setLoadingStates((prev) => ({ ...prev, [cat]: true }))
      );

      if (
        source !== "rating" &&
        visibleCategoryNames[0]?.includes("Left & Lean Left")
      ) {
        return;
      }

      const cfg = getSourceConfig(source, true);
      const firstPage =
        source === "databases"
          ? {
              results: normalizeDatabaseSearchSections(
                await getDatabaseSearch(
                  query,
                  filteredDateValue,
                  visibleCategoryNames.map(getDatabaseApiName),
                  1,
                  cfg.count
                ),
                visibleCategoryNames
              ),
            }
          : await fetchCombinedSearch(
              query,
              filteredDateValue,
              visibleCategoryNames,
              `${cfg.count}`,
              "0"
            );

      if (isStaleRequest()) return;

      setCombinedSearch(firstPage.results);
      visibleCategoryNames.forEach((cat) =>
        setLoadingStates((prev) => ({ ...prev, [cat]: false }))
      );
      if (source === "partner-publishers") {
        const orderedPublishers = firstPage.results
          .map(result =>
            publishers.find(pub => pub.categoryName_chr === result.media)
          )
          .filter(Boolean);

        dispatch(setCategories(orderedPublishers));
      } else if (source !== "rating" && source !== "databases") {
        dispatch(setCategories(firstPage.categories));
      }

    } catch (e) {
      if (isStaleRequest()) return;
      console.error("Error fetching data:", e);
      visibleCategoryNames.forEach((cat) =>
        setLoadingStates((prev) => ({ ...prev, [cat]: false }))
      );
    } finally {
      if (!isStaleRequest()) {
        setGlobalLoading(false);
      }
    }
  };

  const fetchMore = async (category) => {
    const cfg = getSourceConfig(source, true);
    if (cfg.count >= cfg.maxResults) return;
    try {
      const nextPage = await fetchCombinedSearch(
        query,
        filteredDateValue,
        [category],
        `${cfg.maxResults - cfg.count}`,
        `${cfg.offset}`
      );

      if (!nextPage || !nextPage.results || !nextPage.results.length) {
        dispatch(setNoFetchMore({ category, value: true }));
        return;
      }

      setCombinedSearch((prev) => {
        const merged = [...prev];
        const newSection = nextPage.results[0];

        if (!newSection) {
          dispatch(setNoFetchMore({ category, value: true }));
          return merged;
        }

        const i = merged.findIndex((s) => s.media === newSection.media);

        if (i > -1) {
          const existingItems = merged[i].webPages?.value ?? [];
          const existingIdentifiers = new Set(
            existingItems.map(item => item.url || item.id).filter(Boolean)
          );
          const newItems = (newSection.webPages?.value ?? []).filter(
            item => {
              const identifier = item.url || item.id;
              return identifier && !existingIdentifiers.has(identifier);
            }
          );

          if (newItems.length > 0) {
            merged[i] = {
              ...merged[i],
              webPages: {
                ...merged[i].webPages,
                value: [
                  ...existingItems,
                  ...newItems,
                ],
              },
            };
          } else {
            // No new unique items found - set noFetchMore
            dispatch(setNoFetchMore({ category, value: true }));
          }
        } else {
          merged.push(newSection);
        }
        return merged;
      });
    } catch (error) {
      console.error('Error fetching more results:', error);
    } finally {
    }
  };
  useEffect(() => {

    if (!currentTimeframe && filteredDateValue == null) {
      return;
    }
    if (query && categoriesCopy.length > 0) fetchCombinedSearchData();
    return () => {
      searchRequestRef.current += 1;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [query, filteredDateValue, categoriesCopy, source, currentTimeframe]);

  return { combinedSearch, fetchMore };
};

export default useNewsData;
